import { useEffect, useRef } from 'react';
import { getCookie } from '@/utils/cookie';
import { isRecorderMode } from '@/utils/recorderMode';

/**
 * WEHAGO 써드파티 헤더(ONE AI 봇 등 포함)를 로드하는 훅입니다.
 * 기존 wehago-conference의 AppContainer.js 내 getHeader 로직을 현대화하여 동일하게 유지합니다.
 */
export const useThirdPartyHeader = () => {
  const headerLoadAttempts = useRef(0);
  const maxLoadAttempts = 5;

  useEffect(() => {
    // recorder(Jibri) 모드: 로그인 토큰이 없어 항상 5회 재시도 후 실패하고,
    // 회사 헤더/즐겨찾기 등 UI 자체가 녹화와 무관하므로 스크립트 로드를 시도하지 않는다.
    if (isRecorderMode()) return;

    // ─── 포탈 헤더/네비게이터 숨김 ──────────────────────────────────────
    // 써드파티 스크립트가 divPortalHeader, divNavigator를 비동기로 DOM에 삽입하므로
    // MutationObserver로 삽입 시점을 감지하여 즉시 숨긴다.
    const hidePortalUI = () => {
      const divPortalHeader = document.getElementById('divPortalHeader');
      if (divPortalHeader) divPortalHeader.style.display = 'none';
      const divNavigator = document.getElementById('divNavigator');
      if (divNavigator) divNavigator.style.display = 'none';
      return !!(divPortalHeader || divNavigator);
    };

    // 이미 DOM에 존재하면 즉시 숨김
    hidePortalUI();

    // 아직 없으면 Observer로 감지
    const observer = new MutationObserver(() => {
      if (hidePortalUI()) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // ─── 써드파티 헤더 스크립트 로드 ─────────────────────────────────────
    const getHeader = () => {
      const authToken = getCookie('AUTH_A_TOKEN');

      // 토큰이 없고 아직 최대 시도 횟수에 도달하지 않았다면 재시도
      if (!authToken) {
        if (headerLoadAttempts.current < maxLoadAttempts) {
          console.log(`===== 써드파티헤더 로드 시도 ${headerLoadAttempts.current + 1}/${maxLoadAttempts}: 토큰 없음`);
          headerLoadAttempts.current++;

          // 1초 후 재시도
          setTimeout(getHeader, 1000);
          return;
        } else {
          console.log("===== 써드파티헤더 로드 실패: 최대 시도 횟수 초과");
          return;
        }
      }

      // window.closeWehagoAiBot 함수가 이미 존재하면 이미 로드된 상태
      if (typeof (window as any).closeWehagoAiBot === 'function') {
        console.log("===== 써드파티헤더가 이미 로드되어 있습니다.");
        return;
      }

      // 중복 스크립트 요소 주입 방지 (Strict Mode 등에서 중복 실행 방지)
      if (document.getElementById('wehago-global-live-script')) {
        return;
      }

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.id = 'wehago-global-live-script';

      const appType = import.meta.env.VITE_APP_TYPE;
      console.log({appType})

      switch (appType) {
        case "productDev":
          // 로컬 개발 환경(yarn dev)에서는 Vite proxy를 경유하여 Mixed Content/CORS 방지
          // 원본: http://172.16.114.131/third_party/baseComponents/wehago.global.dev.js
          script.src = "http://172.16.114.131/third_party/baseComponents/wehago.global.dev.js";
          break;
        case "wehago":
        case "video2":
          script.src = "https://static.wehago.com/baseComponents/wehago.global.live.js";
          break;
        case "wehagov":
          script.src = "https://static.wehagov.com/baseComponents/wehagov.global.live.js";
          break;
        case "wehagom":
          script.src = "https://static.wehago.com/baseComponents/wehago.global.md.js";
          break;
        default:
          break;
      }

      // src가 없으면 렌더링 불가 환경이므로 종료
      if (!script.src) return;

      // 로드 성공 시 실행
      script.onload = () => {
        console.log("===== 써드파티헤더 스크립트 로드 완료");
        headerLoadAttempts.current = 0; // 성공 시 카운터 리셋
      };

      // 로드 실패 시 재시도
      script.onerror = () => {
        if (headerLoadAttempts.current < maxLoadAttempts) {
          console.log(`===== 써드파티헤더 로드 실패 ${headerLoadAttempts.current + 1}/${maxLoadAttempts}: 재시도 중...`);
          headerLoadAttempts.current++;

          // 스크립트 삭제 및 2초 후 재시도
          script.remove();
          setTimeout(getHeader, 2000);
        } else {
          console.log("===== 써드파티헤더 로드 실패: 최대 시도 횟수 초과");
        }
      };

      document.body.appendChild(script);
    };

    getHeader();

    return () => {
      observer.disconnect();
    };
  }, []);
};
